<template>
  <v-list>
    <v-list-group no-action
                  style="border-radius: 5px!important"
                  class="kbGray kbWhite--text"
                  v-model="listGroupModelingVariable">
      <template v-slot:activator>
        <v-list-item-action>
          <template>
            <v-icon color="white">
              mdi-calendar
            </v-icon>
          </template>
        </v-list-item-action>
        <v-list-item-content class="ml-6">
          <v-list-item-title class="white--text">
            {{ isTaskDone ? $t('general.completedTasks') : $t('general.currentTasks') }}
          </v-list-item-title>
        </v-list-item-content>
      </template>

      <template v-if="tasks.length === 0">
        <div class="kbLight pa-3 text-center">
          <span class="kbGrayText--text">{{ isTaskDone ? $t('general.noCompletedTasks') : $t('general.noTasksToDo') }}</span>
        </div>
      </template>

      <template v-else>
        <template v-for="(task, index) in tasks">
          <v-list-item two-line
                       class="kbLight px-4"
                       :key="`task-${index}`">
            <v-list-item-content>
              <v-list-item-title class="kbDarkBlueText--text mb-2">
                <div class="d-flex align-center">
                  <InlineEditor field-type="text"
                                :value="task.name"
                                @onSave="updateTask({ key: 'name', value: $event, task })">
                    <span class="text-capitalize" style="font-size: 18px">
                      {{ task.name }}
                    </span>
                  </InlineEditor>
                  <v-chip :color="colorBasedOnDiff(task.diff)"
                          text-color="white"
                          x-small
                          class="pa-3"
                          label
                          style="cursor: pointer"
                          v-if="!isTaskDone && task.date">
                    <v-avatar left>
                      <v-icon small>mdi-ballot-outline</v-icon>
                    </v-avatar>
                    {{ new Date(task.date).toLocaleString('default', { month: 'short' }).toUpperCase() + ' ' + new Date(task.date).getDate() }}
                  </v-chip>
                </div>
              </v-list-item-title>

              <v-list-item-subtitle v-if="typesOfTask.length">
                <InlineEditor :value="task.type"
                              field-type="radio"
                              :label="$t('general.type')"
                              :options="typesOfTask"
                              option-label="type"
                              option-value="type"
                              @onSave="updateTask({ key: 'type', value: $event, task })">
                  <div class="d-flex">
                    <span class="kbDarkBlueText--text mr-2">
                      {{ $t('general.type') }}:
                    </span>
                    <v-chip color="kbBlue"
                            dark
                            small>
                      {{ task.type }}
                    </v-chip>
                  </div>
                </InlineEditor>
              </v-list-item-subtitle>

              <v-list-item-subtitle>
                <div class="d-flex align-center">
                  <div class="d-flex align-center mr-2">
                    <v-menu :close-on-content-click="false">
                      <template v-slot:activator="{ on }">
                        <v-btn :color="mainColor"
                               class="mr-1"
                               dark
                               depressed
                               fab
                               height="25px"
                               width="25px"
                               v-on="on"
                               x-small>
                          <v-icon size="15px">mdi-calendar</v-icon>
                        </v-btn>
                      </template>
                      <v-row>
                        <v-col cols="12">
                          <v-date-picker
                            :color="mainColor"
                            v-model="task.shortDate"
                            full-width
                            :landscape="$vuetify.breakpoint.mdAndUp"
                            @click:date="updateTaskDateTime(task, true)"
                          />
                        </v-col>
                      </v-row>
                    </v-menu>
                    <span class="kbGrayText--text mt-1"
                          style="font-size: 14px">
                      {{ task.shortDate || '' }}
                    </span>
                  </div>

                  <div class="d-flex align-center">
                    <v-menu :close-on-content-click="false" min-width="600px">
                      <template v-slot:activator="{ on }">
                        <v-btn :color="mainColor"
                               class="mr-1"
                               dark
                               depressed
                               fab
                               height="25px"
                               width="25px"
                               v-on="on"
                               x-small>
                          <v-icon size="15px">mdi-clock</v-icon>
                        </v-btn>
                      </template>
                      <div>
                        <v-time-picker
                          :color="mainColor"
                          format="24hr"
                          v-model="task.time"
                          :landscape="$vuetify.breakpoint.mdAndUp"
                          full-width
                          @click:hour="updateTaskDateTime(task)"
                          @click:minute="updateTaskDateTime(task)"
                        />
                      </div>
                    </v-menu>
                    <span class="kbGrayText--text mt-1"
                          style="font-size: 14px">
                      {{ task.time || '' }}
                    </span>
                  </div>
                </div>
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <div class="d-flex align-center">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn :color="isTaskDone ? 'kbDarkBlueText' : 'kbGreen'"
                           icon
                           small
                           v-on="on"
                           @click="updateTask({ key: 'done', value: !isTaskDone, task })">
                      <v-icon>
                        {{ isTaskDone ? 'mdi-arrow-left-circle-outline' : 'mdi-arrow-right-circle-outline' }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ isTaskDone ? $t('general.moveToUndoneTasks') : $t('general.moveToDoneTasks') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn color="kbRed"
                           icon
                           :loading="removingTask.status && task.id === removingTask.id"
                           small
                           v-on="on"
                           @click="removeTask(task.id)">
                      <v-icon>mdi-close-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('general.deleteTask') }}</span>
                </v-tooltip>
              </div>
            </v-list-item-action>
          </v-list-item>

          <v-row class="kbLight"
                 no-gutters
                 :key="`${index}-task-observation`">
            <v-col cols="12" class="pb-2 px-1">
              <InlineEditor field-type="textLong"
                            :value="task.observation"
                            @onSave="updateTask({ key: 'observation', value: $event, task })">
                <span class="px-3 kbGrayText--text font-weight-light"
                      style="font-size: 16px">
                  {{ task.observation || $t('general.observations')}}
                </span>
              </InlineEditor>
            </v-col>
            <v-col cols="12"
                   class="py-2"
                   v-if="tasks.length > 1 && index < tasks.length - 1">
              <v-divider/>
            </v-col>
          </v-row>
        </template>
      </template>
    </v-list-group>
  </v-list>
</template>

<script>
import Tasks from '@/mixins/Tasks'
import notifications from '@/libs/notifications'
import { mapGetters } from 'vuex'
import TimeZones from '@/mixins/TimeZones'
export default {
  computed: {
    ...mapGetters(['appUuid', 'companyUuid'])
  },
  data () {
    return {
      listGroupModelingVariable: true,
      removingTask: {
        status: false,
        id: -1
      }
    }
  },
  methods: {
    async removeTask (taskId) {
      this.removingTask.status = true
      this.removingTask.id = taskId
      try {
        await this.$store.dispatch('deleteUserTask', {
          companyId: this.companyUuid,
          appId: this.appUuid,
          userId: this.userId,
          taskId
        })
        await this.$emit('getTasks')
        this.removingTask.status = false
        this.removingTask.id = -1
      } catch (e) {
        this.removingTask.status = false
        this.removingTask.id = -1
        notifications.error(e)
        throw e
      }
    },
    async updateTask ({ key, value, task }) {
      try {
        const taskCopy = JSON.parse(JSON.stringify(task))
        taskCopy[key] = value
        // Showing taskOutcomeDialog if changing key status === 'done' and value === true
        if (key === 'done' && value && (task.type === 'call' || task.type === 'email' || task.type === 'social')) {
          this.$emit('showTaskOutcomeDialog', { task: taskCopy, userId: this.userId })
          return
        }
        const rowIndex = this.allTasks.map(item => item.id).indexOf(taskCopy.id)
        this.$store.commit('SET_TASKS_ROW', { rowIndex, task: taskCopy })
        await this.$store.dispatch('updateUserTask', {
          companyId: this.companyUuid,
          appId: this.appUuid,
          userId: this.userId,
          task: taskCopy
        })
        this.$emit('getTasks')
      } catch (e) {
        notifications.error(this.$t('general.errorUpdatingTasks'))
        throw e
      }
    },
    async updateTaskDateTime (task, isDate = false) {
      const { shortDate, time, ...all } = task
      let fullDate = this.convertTZToUserTZ(`${shortDate} ${time}`, this.userKeybeTimeZone.label)
      fullDate = this.convertTZ(fullDate)

      await this.$store.dispatch('updateUserTask', {
        companyId: this.companyUuid,
        appId: this.appUuid,
        userId: this.userId,
        task: { ...all, date: fullDate }
      })
      this.$emit('getTasks')
    }
  },
  mixins: [Tasks, TimeZones],
  props: {
    allTasks: {
      type: Array,
      required: true
    },
    mainColor: {
      type: String,
      required: true
    },
    tasks: {
      type: Array,
      default: () => []
    },
    isTaskDone: {
      type: Boolean,
      default: false
    },
    typesOfTask: {
      type: Array,
      default: () => []
    },
    userId: {
      type: String,
      required: true
    }
  }
}
</script>
